import React from 'react';
import { fmt } from '../../../../../utils/numberFormat';

export type PhaseType = 'L1' | 'L2' | 'L3' | 'N' | 'Ges';

export interface ThresholdBarProps {
  phase: PhaseType;
  currentValue: number;
  minValue?: number;
  maxValue?: number;
  /** Maximum threshold (e.g., fuse size 225A) - defines the end of the scale */
  maxThreshold: number;
  /** Warning threshold (e.g., 200A) - where yellow zone starts. Undefined = no threshold set (no colored zones) */
  warningThreshold?: number;
  unit: string;
  /** If true, allows negative values (centered bar) */
  allowNegative?: boolean;
}

const formatValue = (value: number): string => {
  if (Number.isNaN(value) || !Number.isFinite(value)) return '-';
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (absValue >= 100) return sign + fmt(absValue, 0);
  if (absValue >= 10) return sign + fmt(absValue, 1);
  return sign + fmt(absValue, 2);
};

/**
 * Lemonbeat-style horizontal threshold bar with color zones.
 * Shows current value, min/max range, and threshold markers.
 * Supports negative values with centered bar display.
 */
export const ThresholdBar: React.FC<ThresholdBarProps> = ({
  phase,
  currentValue,
  minValue,
  maxValue,
  maxThreshold,
  warningThreshold,
  allowNegative = false,
}) => {
  // Whether thresholds are available (from device)
  const hasThresholds = warningThreshold != null;

  // Use current value as fallback for min/max if not provided
  const effectiveMin = minValue ?? currentValue;
  const effectiveMax = maxValue ?? currentValue;

  // Check if we have negative values
  const hasNegativeValues = allowNegative && (effectiveMin < 0 || effectiveMax < 0 || currentValue < 0);

  // For negative-capable bars, we center at 0
  // Scale goes from -maxThreshold to +maxThreshold
  const scaleMax = hasNegativeValues ? maxThreshold : maxThreshold * 1.1;
  const scaleMin = hasNegativeValues ? -maxThreshold : 0;
  const scaleRange = scaleMax - scaleMin;

  const toPercent = (value: number) => {
    const clamped = Math.min(scaleMax, Math.max(scaleMin, value));
    return ((clamped - scaleMin) / scaleRange) * 100;
  };

  // For centered bars, warning/critical apply to absolute values
  const warningPercent = hasThresholds ? toPercent(warningThreshold) : 0;
  const criticalPercent = hasThresholds ? toPercent(maxThreshold) : 0;
  const negWarningPercent = hasNegativeValues && hasThresholds ? toPercent(-warningThreshold) : 0;
  const negCriticalPercent = hasNegativeValues && hasThresholds ? toPercent(-maxThreshold) : 0;
  const centerPercent = hasNegativeValues ? 50 : 0;

  const currentPercent = toPercent(currentValue);
  const minPercent = toPercent(effectiveMin);
  const maxPercent = toPercent(effectiveMax);

  // Calculate filled range position
  const rangeLeft = Math.min(minPercent, maxPercent);
  const rangeWidth = Math.abs(maxPercent - minPercent);

  // Fixed color for current value marker - info blue (same as other components)
  const currentBgColor = 'bg-info-500';
  // Fixed color for the min/max range - gray
  const rangeColor = 'bg-gray-500 dark:bg-gray-400';

  return (
    <div className="py-3 border-b border-gray-100 dark:border-primary-700 last:border-b-0">
      {/* The bar */}
      <div className="flex items-center gap-4">
        {/* Phase label */}
        <span className="w-12 text-base font-bold text-gray-800 dark:text-primary-100 shrink-0">
          {phase}
        </span>

        {/* Bar container - with space for labels above and below */}
        <div className="flex-1 relative h-16 pt-5 pb-5">
          {/* Background zones */}
          {!hasThresholds ? (
            // No thresholds set - plain gray bar
            <div className="absolute top-5 left-0 right-0 bottom-5 flex rounded-lg overflow-hidden border-2 border-gray-300 dark:border-primary-500">
              <div className="h-full w-full bg-gray-200 dark:bg-primary-600" />
            </div>
          ) : hasNegativeValues ? (
            // Centered bar for negative values
            <div className="absolute top-5 left-0 right-0 bottom-5 flex rounded-lg overflow-hidden border-2 border-gray-300 dark:border-primary-500">
              {/* Left red zone (negative critical) */}
              <div
                className="h-full bg-red-400 dark:bg-red-500"
                style={{ width: `${negCriticalPercent}%` }}
              />
              {/* Left orange zone (negative warning) */}
              <div
                className="h-full bg-orange-400 dark:bg-orange-500"
                style={{ width: `${negWarningPercent - negCriticalPercent}%` }}
              />
              {/* Left green zone (negative normal) */}
              <div
                className="h-full bg-green-400 dark:bg-green-600"
                style={{ width: `${centerPercent - negWarningPercent}%` }}
              />
              {/* Right green zone (positive normal) */}
              <div
                className="h-full bg-green-400 dark:bg-green-600"
                style={{ width: `${warningPercent - centerPercent}%` }}
              />
              {/* Right orange zone (positive warning) */}
              <div
                className="h-full bg-orange-400 dark:bg-orange-500"
                style={{ width: `${criticalPercent - warningPercent}%` }}
              />
              {/* Right red zone (positive critical) */}
              <div
                className="h-full bg-red-400 dark:bg-red-500"
                style={{ width: `${100 - criticalPercent}%` }}
              />
            </div>
          ) : (
            // Normal bar (0 to max)
            <div className="absolute top-5 left-0 right-0 bottom-5 flex rounded-lg overflow-hidden border-2 border-gray-300 dark:border-primary-500">
              {/* Green zone */}
              <div
                className="h-full bg-green-400 dark:bg-green-600"
                style={{ width: `${warningPercent}%` }}
              />
              {/* Orange zone */}
              <div
                className="h-full bg-orange-400 dark:bg-orange-500"
                style={{ width: `${criticalPercent - warningPercent}%` }}
              />
              {/* Red zone */}
              <div
                className="h-full bg-red-400 dark:bg-red-500"
                style={{ width: `${100 - criticalPercent}%` }}
              />
            </div>
          )}

          {/* Center line for negative-capable bars */}
          {hasNegativeValues && (
            <div
              className="absolute top-5 bottom-5 w-0.5 bg-gray-400 dark:bg-primary-400 z-5"
              style={{ left: '50%' }}
            />
          )}

          {/* Filled range from min to max (violet bar) */}
          {rangeWidth > 0.5 && (
            <div
              className={`absolute top-6 bottom-6 ${rangeColor} rounded opacity-80`}
              style={{
                left: `calc(${rangeLeft}% + 2px)`,
                width: `calc(${rangeWidth}% - 4px)`
              }}
            />
          )}

          {/* Min value label below bar */}
          {minValue !== undefined && minValue !== maxValue && (
            <div
              className="absolute bottom-0 z-20"
              style={{ left: `${minPercent}%`, transform: 'translateX(-50%)' }}
            >
              <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {formatValue(effectiveMin)}
              </span>
            </div>
          )}

          {/* Max value label below bar */}
          {maxValue !== undefined && minValue !== maxValue && (
            <div
              className="absolute bottom-0 z-20"
              style={{ left: `${maxPercent}%`, transform: 'translateX(-50%)' }}
            >
              <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 whitespace-nowrap">
                {formatValue(effectiveMax)}
              </span>
            </div>
          )}

          {/* Current value indicator (vertical line) */}
          <div
            className="absolute top-5 bottom-5 z-30"
            style={{ left: `${currentPercent}%` }}
          >
            <div className={`absolute top-0 bottom-0 w-1 -translate-x-1/2 ${currentBgColor} shadow-md rounded-full`} />
          </div>
          {/* Current value label above bar */}
          <div
            className="absolute top-0 z-30"
            style={{ left: `${currentPercent}%`, transform: 'translateX(-50%)' }}
          >
            <span className={`px-1.5 py-0.5 text-[10px] font-bold text-white rounded shadow whitespace-nowrap ${currentBgColor}`}>
              {formatValue(currentValue)}
            </span>
          </div>

          {/* Threshold labels below bar (no lines, only numbers) - only when thresholds are set */}
          {hasThresholds && (
            <>
              <div
                className="absolute bottom-0 z-20"
                style={{ left: `${warningPercent}%`, transform: 'translateX(-50%)' }}
              >
                <span className="text-[11px] font-medium text-warning-600 dark:text-warning-400 whitespace-nowrap">
                  {formatValue(warningThreshold)}
                </span>
              </div>
              <div
                className="absolute bottom-0 z-20"
                style={{ left: `${criticalPercent}%`, transform: 'translateX(-50%)' }}
              >
                <span className="text-[11px] font-medium text-error-600 dark:text-error-400 whitespace-nowrap">
                  {formatValue(maxThreshold)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThresholdBar;
